/**
 * RAE — Booking wizard state reducer.
 *
 * Local-only state holder. Real submission hits PLANNED `/v3/bookings/*` once it lands.
 */

import { useReducer, useCallback, type Dispatch } from 'react';
import type {
  BookingWizardState,
  MeetingMethod,
  RaeBranchChoice,
  RaeService,
  UploadedFile,
  WizardStepIndex,
} from './types';

export type BookingAction =
  | { type: 'setService'; service: RaeService }
  | { type: 'setMethod'; method: MeetingMethod }
  | { type: 'setBranch'; branch: RaeBranchChoice }
  | { type: 'setDateTime'; date: string; time: string }
  | { type: 'addFile'; file: UploadedFile }
  | { type: 'removeFile'; id: string }
  | { type: 'setDetails'; details: Partial<Pick<BookingWizardState, 'firstName' | 'lastName' | 'email' | 'phone' | 'smsReminders' | 'fundingAmountRange' | 'notes'>> }
  | { type: 'setConfirmation'; confirmationNumber: string; officerDisplayName: string; officerInitials: string }
  | { type: 'reset' };

export const INITIAL_STATE: BookingWizardState = {
  service: null,
  method: null,
  branch: null,
  date: null,
  time: null,
  files: [],
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  smsReminders: true,
  fundingAmountRange: '',
  notes: '',
  confirmationNumber: null,
  officerDisplayName: null,
  officerInitials: null,
};

export function bookingReducer(
  state: BookingWizardState,
  action: BookingAction,
): BookingWizardState {
  switch (action.type) {
    case 'setService':
      return { ...state, service: action.service };
    case 'setMethod':
      return { ...state, method: action.method };
    case 'setBranch':
      return { ...state, branch: action.branch };
    case 'setDateTime':
      return { ...state, date: action.date, time: action.time };
    case 'addFile':
      return { ...state, files: [...state.files, action.file] };
    case 'removeFile':
      return { ...state, files: state.files.filter((f) => f.id !== action.id) };
    case 'setDetails':
      return { ...state, ...action.details };
    case 'setConfirmation':
      return {
        ...state,
        confirmationNumber: action.confirmationNumber,
        officerDisplayName: action.officerDisplayName,
        officerInitials: action.officerInitials,
      };
    case 'reset':
      return INITIAL_STATE;
    default:
      return state;
  }
}

export interface UseBookingWizardResult {
  state: BookingWizardState;
  dispatch: Dispatch<BookingAction>;
  /** True if the step at the given index has its prerequisites met. */
  canAdvanceTo: (target: WizardStepIndex) => boolean;
}

export function useBookingWizard(): UseBookingWizardResult {
  const [state, dispatch] = useReducer(bookingReducer, INITIAL_STATE);

  const canAdvanceTo = useCallback(
    (target: WizardStepIndex): boolean => {
      if (target === 0) return true;
      if (target === 1) return !!state.service;
      if (target === 2) return !!state.service && !!state.method;
      if (target === 3)
        return !!state.service && !!state.method && !!state.branch && !!state.date && !!state.time;
      if (target === 4)
        return !!state.service && !!state.method && !!state.branch && !!state.date && !!state.time;
      return false;
    },
    [state],
  );

  return { state, dispatch, canAdvanceTo };
}

/**
 * Pseudo-confirmation generator for the demo path (pre-`/v3/bookings/*`).
 * Real booking submission returns a server-issued code + assigned officer.
 */
export function generateConfirmationCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 5; i += 1) {
    code += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `PCB-${code}`;
}
